import { AxiosResponse } from "axios";
import { IAddressLite, IDistance, IPatientAddress, IValidationResult } from "lobbie";
import { useCallback, useState } from "react";

import { useAxiosGetter, useAxiosPoster, useAxiosPutter } from "src/hooks/useAxios";

import { handleError, isFailedRequest, notify } from "src/utils";

const BASE_ROUTE = "/staff/evv/appointments/address";

type TAddresses = IPatientAddress[] | undefined | void;

export const useAccountPatientAddresses = (): {
    addresses: TAddresses;
    getAddresses: (accountPatientId: number) => Promise<TAddresses>;
    createAddress: (data: Record<string, unknown>) => Promise<TAddresses>;
    updateAddress: (data: Record<string, unknown>) => Promise<TAddresses>;
    getDistance: (
        patientAddressId: number,
        latitude: number,
        longitude: number,
    ) => Promise<IDistance | undefined | void>;
    reverseGeocode: (
        latitude: number,
        longitude: number,
        accountPublicUuid: string | null | undefined,
    ) => Promise<IAddressLite | undefined | void>;
} => {
    const getter = useAxiosGetter();
    const putter = useAxiosPutter();
    const poster = useAxiosPoster();

    const [addresses, setAddresses] = useState<TAddresses>([]);

    const getDistance = useCallback(
        async (patientAddressId: number, latitude: number, longitude: number) => {
            return poster(`${BASE_ROUTE}/distance`, {
                patientAddressId,
                latitude,
                longitude,
            })
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as IDistance | IValidationResult);
                    if (!result) {
                        response?.status &&
                            response.status > 399 &&
                            notify({
                                level: "error",
                                title: "Error calculating distance.",
                                message: "Please refresh and try again.",
                            });
                        return;
                    }
                    if ("success" in result && !result.success) {
                        notify({
                            level: "error",
                            title: "Error initializing.",
                            message: result.message,
                        });
                    } else {
                        return result as IDistance;
                    }
                })
                .catch((error) => {
                    handleError(error);
                    return {
                        distanceKilometers: null,
                        distanceMeters: null,
                        distanceMiles: null,
                        distanceYards: null,
                        distanceFeet: null,
                    };
                });
        },
        [poster],
    );

    const getAddresses = useCallback(
        async (accountPatientId: number) => {
            return getter(`${BASE_ROUTE}/patient/${accountPatientId}`)
                .then((response: AxiosResponse | void) => {
                    const result =
                        response && (response.data as IPatientAddress[] | IValidationResult);
                    if (!result) {
                        response?.status &&
                            response.status > 399 &&
                            notify({
                                level: "error",
                                title: "Error fetching address.",
                                message: "Please refresh and try again. Hi",
                            });
                        return;
                    }
                    if ("success" in result && !result.success) {
                        notify({
                            level: "error",
                            title: "Error initializing.",
                            message: result.message,
                        });
                    } else {
                        setAddresses(result as IPatientAddress[]);
                        return result as IPatientAddress[];
                    }
                })
                .catch((error) => {
                    handleError(error);
                    return [];
                });
        },
        [getter],
    );

    const createAddress = useCallback(
        async (data: Record<string, unknown>) => {
            return poster(`${BASE_ROUTE}/create`, data)
                .then((response: AxiosResponse | void) => {
                    const result =
                        response && (response.data as IPatientAddress[] | IValidationResult);
                    if (!result) {
                        response?.status &&
                            response.status > 399 &&
                            notify({
                                level: "error",
                                title: "Error creating address.",
                                message: "Please refresh and try again.",
                            });
                        return;
                    }
                    if ("success" in result && !result.success) {
                        notify({
                            level: "error",
                            title: "Error initializing.",
                            message: result.message,
                        });
                    } else {
                        setAddresses(result as IPatientAddress[]);
                        return result as IPatientAddress[];
                    }
                })
                .catch((error) => {
                    handleError(error);
                    return [];
                });
        },
        [poster],
    );

    const updateAddress = useCallback(
        async (data: Record<string, unknown>) => {
            return putter(`${BASE_ROUTE}/update`, data)
                .then((response: AxiosResponse | void) => {
                    const result =
                        response && (response.data as IPatientAddress[] | IValidationResult);
                    if (!result) {
                        response?.status &&
                            response.status > 399 &&
                            notify({
                                level: "error",
                                title: "Error updating address.",
                                message: "Please refresh and try again.",
                            });
                        return;
                    }
                    if ("success" in result && !result.success) {
                        notify({
                            level: "error",
                            title: "Error initializing.",
                            message: result.message,
                        });
                    } else {
                        setAddresses(result as IPatientAddress[]);
                        return result as IPatientAddress[];
                    }
                })
                .catch((error) => {
                    handleError(error);
                    return [];
                });
        },
        [putter],
    );

    const reverseGeocode = useCallback(
        async (latitude: number, longitude: number) => {
            const requestMethod = poster;
            const route = `${BASE_ROUTE}/reverse_geocode`;
            return requestMethod(route, {
                latitude,
                longitude,
            })
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as IAddressLite | IValidationResult);
                    if (!result) {
                        notify({
                            level: "error",
                            title: "Error fetching the address.",
                            message: "Please refresh and try again.",
                        });

                        return;
                    }
                    if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: "Error initializing.",
                            message: (result as IValidationResult).message,
                        });
                    } else {
                        return result as IAddressLite;
                    }
                })
                .catch((error) => {
                    handleError(error);
                    return undefined;
                });
        },
        [poster],
    );

    return {
        addresses,
        getAddresses,
        createAddress,
        updateAddress,
        getDistance,
        reverseGeocode,
    };
};
